import React, { Component } from 'react'

import classnames from 'classnames'
import isFunction from 'lodash/isFunction'
import throttle from 'lodash/throttle'

import { configureRange } from './slider-utils'

import './RangeSlider.scss'

export interface IRange {
  value: number
  step?: number
}

export interface IProps {
  id?: string
  name?: string
  className?: string
  children?: React.ReactNode
  value: number
  defaultValue?: number
  disabled?: boolean
  minLabel?: string
  maxLabel?: string
  onChange?: (value: number) => void
  onChangeComplete?: (e: any) => void
  userTracking?: any
  trackElement?: string
  range: IRange[]
  rf?: string
}

/**
 * Range slider input
 *
 *
 * @param className (string, optional) - Input id
 * @param id (string, optional) - Hidden input id
 * @param name (string, optional) - Hidden input name
 * @param value (number, required) - Contolled range value
 * @param defaultValue (number, optional) - Default value
 * @param disabled (boolean, optional) - Disable range slider
 * @param minLabel (string, optional) - Min. label
 * @param maxLabel (string, optional) - Max. label
 * @param onChange (void: number, required) - On change handler
 * @param onChangeComplete (void: number, optional) - On last change handler
 * @param range (Range[], required) - Array with value + step object
 * which define range of values and steps between them.
 *
 */
export default class RangeSlider extends Component<IProps, any> {
  private sliderRect: any
  private slider: any

  componentWillMount() {
    this.handleChange = throttle(this.handleChange, 200)
    this.setInitialState(this.props)
  }

  componentDidMount() {
    window.addEventListener('touchmove', this.handleTouchMove)
    window.addEventListener('touchend', this.handleMouseUp)
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('mouseup', this.handleMouseUp)
  }

  componentWillUnmount() {
    //  this.handleChange.cancel()
    window.removeEventListener('touchmove', this.handleTouchMove)
    window.removeEventListener('touchend', this.handleMouseUp)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mouseup', this.handleMouseUp)
  }

  componentWillReceiveProps(nextProps: any) {
    if (nextProps.range && nextProps.range !== this.props.range) {
      this.setInitialState(nextProps)
    }
    if (nextProps.value !== this.props.value && nextProps.value !== this.state.value) {
      const value = this.state.range.ensureValue(nextProps.value)
      this.setState({ value })
    }
  }

  setInitialState = (props: any) => {
    const range = configureRange(props.range)
    const value = range.ensureValue(props.value || props.defaultValue)
    const currentStep = range.getStepForValue(value)
    this.setState({ value, range, currentStep })
  }

  stepUp = (amount: any) => {
    const { range, currentStep } = this.state
    const nextStep = currentStep + amount
    if (nextStep <= range.maxStep) {
      const nextValue = range.getValueForStep(nextStep)
      this.setState({ currentStep: nextStep, value: nextValue })
    }
  }

  stepDown = (amount: any) => {
    const { range, currentStep } = this.state
    const nextStep = currentStep - amount
    if (nextStep >= range.minStep) {
      const nextValue = range.getValueForStep(nextStep)
      this.setState({ currentStep: nextStep, value: nextValue })
    }
  }

  handleChange = () => {
    const { value } = this.state
    const { onChange } = this.props
    if (onChange) {
      onChange(value)
    }
  }

  handleChangeComplete = () => {
    const { value } = this.state
    const { onChangeComplete, userTracking, trackElement } = this.props
    if (onChangeComplete) {
      userTracking && userTracking.onSlideEnd(trackElement)
      onChangeComplete(value)
    }
  }

  handleMouseUp = (e: any) => {
    if (this.state.pressed) {
      this.setState({ pressed: false })
      this.handleChangeComplete()
    }
  }

  handleMouseMove = (e: any) => {
    if (this.state.pressed) {
      this.handleMove(e)
    }
  }

  handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    this.handlePress()
    this.handleMove(e)
  }

  handleTouchMove = (e: any) => {
    if (this.state.pressed) {
      e.preventDefault()
      this.handleMouseMove(e.touches[0])
    }
  }

  handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    this.handlePress()
    this.handleMove(e.touches[0])
  }

  handlePress = () => {
    this.sliderRect = this.slider.getBoundingClientRect()
    this.setState({ pressed: true })
  }

  handleMove = (e: any) => {
    const { clientX } = e
    const { disabled } = this.props
    const { range } = this.state
    const { width, left, right } = this.sliderRect

    if (!clientX || disabled) {
      return
    }

    let position
    if (clientX < left) {
      position = 0
    } else if (clientX > right) {
      position = right - left
    } else {
      position = clientX - left
    }
    const currentStep = Math.round((position / width) * range.maxStep)
    const value = range.getValueForStep(currentStep)

    if (value !== this.state.value || currentStep !== this.state.currentStep) {
      this.setState({ value, currentStep }, this.handleChange)
    }
  }

  render() {
    const { id, name, disabled, children, className, minLabel, maxLabel, rf } = this.props
    const { value, range, currentStep } = this.state

    const offset = (currentStep / range.maxStep) * 100
    const offsetStyle = { left: `${offset}%` }
    const progressStyle = { width: `${offset}%` }

    return (
      <div
        className={classnames('cs-step-range-slider', className)}
        onMouseDown={this.handleMouseDown}
        ref={(node: any) => (this.slider = node)}
        data-testid={rf}
      >
        <div className='cs-step-range-slider__track' />
        <div className='cs-step-range-slider__progress' style={progressStyle} />
        <div
          className='cs-step-range-slider__handle'
          onTouchStart={this.handleTouchStart}
          onMouseDown={this.handleMouseDown}
          style={offsetStyle}
        >
          <div
            className='cs-step-range-slider__thumb'
            aria-valuemin={range.minValue}
            aria-valuemax={range.maxValue}
            aria-valuenow={value}
            role='slider'
            id='pointer'
          />
          {isFunction(children) ? children(value) : children}
        </div>
        <div className='cs-step-range-slider__labels'>
          <span className='cs-step-range-slider__labels--left'>{minLabel}</span>
          <span className='cs-step-range-slider__labels--right'>{maxLabel}</span>
        </div>
        <input type='hidden' id={id} name={name} disabled={disabled} />
      </div>
    )
  }
}
